import { useRef, useState } from 'react';
import { noop } from 'lodash';

const useConfirmChanges = <Data = void>(shouldShowConfirmChanges: boolean = true) => {
  const [displayConfirmChanges, setDisplayConfirmChanges] = useState(false);
  const afterConfirmChangesCallbackRef = useRef(noop);
  const confirmChangesDataRef = useRef<Data>();

  const resetConfirmChanges = () => {
    setDisplayConfirmChanges(false);
    afterConfirmChangesCallbackRef.current = noop;
    confirmChangesDataRef.current = undefined;
  };

  const onConfirmUnsavedChanges = () => {
    afterConfirmChangesCallbackRef.current();
  };

  const useCallbackWithConfirmChanges = <Args extends unknown[]>(
    callback: (...args: Args) => void,
    getConfirmChangesData?: (...args: Args) => Data,
  ) => {
    return (...args: Args) => {
      if (!shouldShowConfirmChanges) {
        callback(...args);

        return;
      }

      if (getConfirmChangesData) {
        confirmChangesDataRef.current = getConfirmChangesData(...args);
      }

      afterConfirmChangesCallbackRef.current = () => callback(...args);

      setDisplayConfirmChanges(true);
    };
  };

  return [
    displayConfirmChanges,
    resetConfirmChanges,
    onConfirmUnsavedChanges,
    useCallbackWithConfirmChanges,
    confirmChangesDataRef.current,
  ] as [boolean, () => void, () => void, typeof useCallbackWithConfirmChanges, Data];
};

export default useConfirmChanges;
